import { useMemo } from 'react';
import { MantineSize } from '@mantine/core';
import { usePreferencesStore } from '../../../../stores/preferencesStore';

// ============================================================================
// Size Mappings
// ============================================================================

type FontSizePreference = 'xs' | 'sm' | 'regular' | 'lg' | 'xl';
type UiSizePreference = 'compact' | 'regular' | 'comfortable' | 'spacious';
type LineSpacingPreference = 'tight' | 'regular' | 'relaxed' | 'loose';

/** Map fontSize preference to Mantine size */
const FONT_SIZE_MAP: Record<FontSizePreference, MantineSize> = {
  xs: 'xs',
  sm: 'sm',
  regular: 'md',
  lg: 'lg',
  xl: 'xl',
};

/** Map fontSize preference to numeric pixel values for custom styling */
const FONT_SIZE_PX_MAP: Record<FontSizePreference, number> = {
  xs: 12,
  sm: 14,
  regular: 16,
  lg: 18,
  xl: 20,
};

/** Map uiSize preference to Mantine spacing */
const UI_SIZE_MAP: Record<UiSizePreference, MantineSize> = {
  compact: 'xs',
  regular: 'sm',
  comfortable: 'md',
  spacious: 'lg',
};

/** Map lineSpacing preference to line-height values */
const LINE_SPACING_MAP: Record<LineSpacingPreference, number> = {
  tight: 1.2,
  regular: 1.5,
  relaxed: 1.75,
  loose: 2,
};

// ============================================================================
// Widget Preferences Hook
// ============================================================================

export interface WidgetPreferences {
  /** Mantine-compatible text size */
  textSize: MantineSize;
  /** Text size in pixels for custom styling */
  textSizePx: number;
  /** Mantine-compatible spacing/padding size */
  spacing: MantineSize;
  /** Line height value */
  lineHeight: number;
  /** Whether reduced motion is enabled */
  reducedMotion: boolean;
  /** Whether high contrast mode is enabled */
  highContrast: boolean;
  /** Whether tooltips are enabled */
  enableTooltips: boolean;
  /** Whether to show icon labels */
  showIconLabels: boolean;
  /** Letter spacing - 'normal' or 'wide' */
  letterSpacing: 'normal' | 'wide';
  /** Letter spacing CSS value */
  letterSpacingCss: string;
  /** Heading text size (one step larger than textSize) */
  headingSize: MantineSize;
  /** Small/secondary text size (one step smaller than textSize) */
  smallTextSize: MantineSize;
}

/**
 * Hook to get widget-specific preferences from the global preferences store.
 * Provides Mantine-compatible sizes and values for consistent styling across widgets.
 *
 * @example
 * ```tsx
 * function MyWidget() {
 *   const { textSize, spacing, reducedMotion } = useWidgetPreferences();
 *
 *   return (
 *     <Stack gap={spacing}>
 *       <Text size={textSize}>Hello World</Text>
 *     </Stack>
 *   );
 * }
 * ```
 */
export function useWidgetPreferences(): WidgetPreferences {
  const fontSize = usePreferencesStore((state) => state.fontSize);
  const uiSize = usePreferencesStore((state) => state.uiSize);
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const reducedMotion = usePreferencesStore((state) => state.reducedMotion);
  const highContrast = usePreferencesStore((state) => state.highContrast);
  const enableTooltips = usePreferencesStore((state) => state.enableTooltips);
  const showIconLabels = usePreferencesStore((state) => state.showIconLabels);
  const letterSpacing = usePreferencesStore((state) => state.letterSpacing);

  return useMemo(() => {
    const textSize = FONT_SIZE_MAP[fontSize];
    const textSizePx = FONT_SIZE_PX_MAP[fontSize];
    const spacing = UI_SIZE_MAP[uiSize];
    const lineHeight = LINE_SPACING_MAP[lineSpacing];

    // Calculate heading size (one step larger, capped at xl)
    const sizeOrder: MantineSize[] = ['xs', 'sm', 'md', 'lg', 'xl'];
    const currentIndex = sizeOrder.indexOf(textSize);
    const headingSize = sizeOrder[Math.min(currentIndex + 1, sizeOrder.length - 1)];
    const smallTextSize = sizeOrder[Math.max(currentIndex - 1, 0)];

    return {
      textSize,
      textSizePx,
      spacing,
      lineHeight,
      reducedMotion,
      highContrast,
      enableTooltips,
      showIconLabels,
      letterSpacing,
      letterSpacingCss: letterSpacing === 'wide' ? '0.05em' : 'normal',
      headingSize,
      smallTextSize,
    };
  }, [
    fontSize,
    uiSize,
    lineSpacing,
    reducedMotion,
    highContrast,
    enableTooltips,
    showIconLabels,
    letterSpacing,
  ]);
}

/**
 * Get widget animation duration based on reduced motion preference
 */
export function useWidgetAnimation(): {
  duration: number;
  enabled: boolean;
} {
  const reducedMotion = usePreferencesStore((state) => state.reducedMotion);

  return useMemo(
    () => ({
      duration: reducedMotion ? 0 : 200,
      enabled: !reducedMotion,
    }),
    [reducedMotion]
  );
}

// ============================================================================
// Size Variant Text Scaling
// ============================================================================

/** Text scale factors for different size variants */
const SIZE_VARIANT_SCALE: Record<string, number> = {
  // Stat widget variants
  tiny: 0.75,
  small: 0.85,
  medium: 1.0,
  large: 1.15,
  // Stat row variants
  compact: 0.85,
  standard: 1.0,
  wide: 1.0,
  full: 1.1,
};

/** Get pixel size scaled by variant */
function getScaledPx(basePx: number, variant?: string): number {
  const scale = variant ? SIZE_VARIANT_SCALE[variant] ?? 1.0 : 1.0;
  return Math.round(basePx * scale);
}

/** Get Mantine size for a scaled value */
function getMantineSizeForPx(px: number): MantineSize {
  if (px <= 12) return 'xs';
  if (px <= 14) return 'sm';
  if (px <= 16) return 'md';
  if (px <= 18) return 'lg';
  return 'xl';
}

export interface SizeVariantTextSizes {
  /** Scaled heading size for the variant */
  headingSize: MantineSize;
  /** Scaled body text size for the variant */
  textSize: MantineSize;
  /** Scaled small/secondary text size for the variant */
  smallTextSize: MantineSize;
  /** Heading size in pixels */
  headingPx: number;
  /** Body text size in pixels */
  textPx: number;
  /** Small text size in pixels */
  smallTextPx: number;
  /** Icon size appropriate for the variant */
  iconSize: number;
}

/**
 * Get text sizes scaled for a specific widget size variant.
 * Useful for adapting text sizes based on widget dimensions.
 *
 * @param sizeVariant - The widget's size variant (e.g., 'tiny', 'small', 'medium', 'large')
 * @returns Scaled text sizes for use in widgets
 *
 * @example
 * ```tsx
 * function MyStatWidget({ settings }: WidgetProps) {
 *   const { textSize, headingSize, iconSize } = useVariantTextSizes(settings?.sizeVariant);
 *
 *   return (
 *     <Box>
 *       <ThemeIcon size={iconSize}><IconChart /></ThemeIcon>
 *       <Text size={headingSize}>42</Text>
 *       <Text size={textSize}>Active Users</Text>
 *     </Box>
 *   );
 * }
 * ```
 */
export function useVariantTextSizes(sizeVariant?: string): SizeVariantTextSizes {
  const { textSizePx } = useWidgetPreferences();

  return useMemo(() => {
    // Base sizes from user preferences
    const baseHeadingPx = textSizePx + 4; // heading is slightly larger
    const baseTextPx = textSizePx;
    const baseSmallPx = Math.max(10, textSizePx - 2);
    const baseIconSize = Math.max(14, textSizePx);

    // Scale based on variant
    const headingPx = getScaledPx(baseHeadingPx, sizeVariant);
    const textPx = getScaledPx(baseTextPx, sizeVariant);
    const smallTextPx = getScaledPx(baseSmallPx, sizeVariant);
    const iconSize = getScaledPx(baseIconSize, sizeVariant);

    return {
      headingSize: getMantineSizeForPx(headingPx),
      textSize: getMantineSizeForPx(textPx),
      smallTextSize: getMantineSizeForPx(smallTextPx),
      headingPx,
      textPx,
      smallTextPx,
      iconSize,
    };
  }, [textSizePx, sizeVariant]);
}

export default useWidgetPreferences;
